import * as _ from 'underscore'
import DevExpress from 'devextreme'
import ArrayStore from 'devextreme/data/array_store'

import { Enums } from "../../models/common"
import { ColumnModel, CorrespondingActionTypeModel, FieldModel } from "../../modules/api/api.models"
import { EventsNames, ConvertHelper, LocalizationHelper, ToastHelper, LogHelper, CommonHelper } from "../../helpers"
import { USER_SESSION_SERVICE_NAME, UserSessionService } from "../user-session"
import { OpActionType, OpActionData, OpAction } from "../../models/core"
import { DataValue } from "../../models/local"
import { ColumnInfo } from '../../models/custom'
import { OpActionPacket } from '../../models/daq'
import { DataProviderListResult, DataProviderRequest } from '../../data-provider'
import { GridService, GRID_SERVICE_NAME } from '../grid'

//#region Public Types

export const ACTION_SERVICE_NAME = 'actionService'
export const ACTION_SERVICE_PROVIDER_NAME = 'actionServiceProvider'

//#endregion

export class ActionService {

    public static get LOG_PREFIX(): string { return '[SIMAX.Module.Common.Action]' }
    public static get REFERENCE_TYPE(): Enums.ReferenceType { return Enums.ReferenceType.IdAction };
    public static get SIMAX_MODULE_NAME(): Enums.SimaxModuleName { return Enums.SimaxModuleName.Action }
    public static get FILE_NAME_PREFIX(): string { return 'action' };

    public static get TreeKeyFieldName() { return 'idTreeItem' }
    public static get TreeParentFieldName() { return 'idParentTreeItem' }

    // #region Ctor

    constructor(
        private userSession: UserSessionService,
        private gridService: GridService
    ) { }

    // #endregion


    // #region Public Methods

    /**run action directly, without popup with parameters */
    public runAction($scope: ng.IScope, action: CorrespondingActionTypeModel, popupId: number) {
        $scope.$root.$broadcast(EventsNames.RUN_ACTION_DIRECTLY + popupId, action)
    }

    public getCorrespondingActionType(referenceIds: number[], referenceType: Enums.ReferenceType): JQueryPromise<OpActionType[]> {
        return this.userSession
            .getDataProvider()
            .Action
            .getCorrespondingActionType(referenceIds, referenceType)
            .then((actionTypes: OpActionType[]) => {
                return actionTypes
            })
            .fail((error: any) => {
                const errorMsg: string = ConvertHelper.getRequestErrorMessage(error)
                ToastHelper.showError(errorMsg || LocalizationHelper.getLocalizedText(LocalizationHelper.Text.ExceptionOccuredPleaseContactAnAdministrator))
            })
    }


    public getActionParameters(idActionType: number, referenceType: Enums.ReferenceType, referenceIds: number[]): JQueryPromise<OpActionData[]> {
        return this.userSession
            .getDataProvider()
            .Action
            .getActionParameters(idActionType, referenceType, referenceIds)
            .then((actionData: OpActionData[]) => {
                return actionData
            })
            .fail((error: any) => {
                const errorMsg: string = ConvertHelper.getRequestErrorMessage(error)
                ToastHelper.showError(errorMsg || LocalizationHelper.getLocalizedText(LocalizationHelper.Text.ExceptionOccuredPleaseContactAnAdministrator))
            })
    }

    public getDataValues(action: CorrespondingActionTypeModel): DataValue[] {
        let dataValues: DataValue[] = [];

        if (!action.isActionDataFixed && _.size(action.fields) > 0) {
            dataValues = action.fields.map((field: FieldModel) => {
                if (field.typeId == Enums.DataTypeClass.Time) {
                    return new DataValue(field.idDataType, field.index, ConvertHelper.stringToDate(field.value));
                }

                return new DataValue(field.idDataType, field.index, field.value);
            })
        }

        return dataValues
    }

    public getActions(columns: ColumnInfo[], pageNbr: number, pageSize: number, customWhereClause: string): JQueryPromise<DataProviderListResult<OpAction>> {
        return this.userSession
            .getDataProvider()
            .Action
            .getActionFilter(columns, pageNbr, pageSize, customWhereClause)
            .fail((error: any) => {
                const errorMsg: string = ConvertHelper.getRequestErrorMessage(error)
                ToastHelper.showError(errorMsg || LocalizationHelper.getLocalizedText(LocalizationHelper.Text.ExceptionOccuredPleaseContactAnAdministrator))
                LogHelper.log(ActionService.LOG_PREFIX, `Error during getting action filter`)
            })
    }

    public getAction(idAction: number): DataProviderRequest<OpAction> {
        const promise: DataProviderRequest<OpAction> = this.userSession
            .getDataProvider()
            .Action
            .getAction(idAction)

        promise.then((result: OpAction) => {
            return result
        })

        promise.catch((error: any) => {
            const errorMsg: string = ConvertHelper.getRequestErrorMessage(error)
            ToastHelper.showError(errorMsg || LocalizationHelper.getLocalizedText(LocalizationHelper.Text.ExceptionOccuredPleaseContactAnAdministrator))
            LogHelper.log(ActionService.LOG_PREFIX, `Error during getting action`)

            return null
        })

        return promise
    }

    public getActionPackets(idAction: number): JQueryPromise<OpActionPacket[]> {
        return this.userSession
            .getDataProvider()
            .Action
            .getActionPackets(idAction)
            .then((packets: OpActionPacket[]) => {
                return packets
            })
            .fail((error: any) => {
                const errorMsg: string = ConvertHelper.getRequestErrorMessage(error)
                ToastHelper.showError(errorMsg || LocalizationHelper.getLocalizedText(LocalizationHelper.Text.ExceptionOccuredPleaseContactAnAdministrator))
                LogHelper.log(ActionService.LOG_PREFIX, `Error during getting action packets`)
            })
    }

    public getMainGridColumns(): JQueryPromise<ColumnModel[]> {
        return $.when(
            this.gridService
                .getColumnModelByCategory(Enums.SimaxDataTypeGroupCategory.WEBSIMAX__MODULE_NAME__GRID_PARAMS, ActionService.SIMAX_MODULE_NAME),
            this.gridService
                .getColumnModelByCategory(Enums.SimaxDataTypeGroupCategory.WEBSIMAX__MODULE_NAME__DEFAULT_GRID_PARAMS, ActionService.SIMAX_MODULE_NAME)
        )
            .then((columns: ColumnModel[], defaultColumns: ColumnModel[]) => {
                let defColExist: boolean = false

                // Check if in defaultColumns are columns which are not in columnsDictionary
                defaultColumns.forEach((defCol: ColumnModel) => {
                    if (defCol.type === Enums.DataTypeClass.Datetime) {
                        defCol.type = Enums.DataTypeClass.Date
                    }

                    defColExist = false
                    columns.forEach((col: ColumnModel) => {
                        if (col.type === Enums.DataTypeClass.Datetime) {
                            col.type = Enums.DataTypeClass.Date
                        }

                        if (defCol.id == col.id) {
                            defColExist = true
                        }
                    })

                    if (defColExist == false) {
                        columns.push(defCol)
                    }
                })

                return $.when(columns, defaultColumns)
            })
    }

    public createDataSourceForExtendedParameters(action: OpAction): ArrayStore {
        const items: any[] = []

        if (action != null && _.size(action.dataList) > 0) {
            const filteredData: OpActionData[] = action.dataList.toArray()

            let treeItemId: number = +new Date()

            for (let dItem of filteredData) {
                if (dItem.dataType == null) {
                    dItem.dataType = this.userSession
                        .getDataProvider()
                        .DataType
                        .getDataTypeLocal(dItem.idDataType)
                }

                const treeItem: any = dItem
                treeItem[ActionService.TreeKeyFieldName] = treeItemId++
                treeItem[ActionService.TreeParentFieldName] = 0

                treeItem.value = ConvertHelper.prepareDataValue(treeItem.value, dItem.dataType)

                items.push(treeItem)
            }

            const grouping: _.Dictionary<OpActionData[]> = _.groupBy(filteredData, 'idDataType')

            if (grouping != null) {
                for (let groupKey in grouping) {
                    const groupedData: OpActionData[] = grouping[groupKey]

                    if (groupedData.length > 1) {
                        const sourceItem: OpActionData = groupedData[0]

                        const groupingRowItem: any = {
                            dataType: sourceItem.dataType,                             // dataType column
                            indexNbr: groupedData.map(row => row.indexNbr).join(';'),  // indexNbr column
                            rows: groupedData,                                         // child rows
                        }

                        groupingRowItem[ActionService.TreeKeyFieldName] = sourceItem.idDataType
                        groupingRowItem[ActionService.TreeParentFieldName] = 0

                        items.push(groupingRowItem)

                        groupedData.forEach((row: OpActionData) => {
                            row[ActionService.TreeParentFieldName] = sourceItem.idDataType
                        })
                    }
                }

                items.push
            }
        }

        return new ArrayStore({
            key: ActionService.TreeKeyFieldName,
            data: items
        })
    }

    public getActionDevicesAnswerColumns(): JQueryPromise<ColumnModel[]> {
        const deferred: JQueryDeferred<ColumnModel[]> = $.Deferred<ColumnModel[]>()

        const columns: ColumnModel[] = []
        columns.push(<any>{ id: this.addresColumnName, caption: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Address) })
        columns.push(<any>{
            id: this.isIncomingColumnName,
            caption: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Direction),
            lookup: {
                dataSource: [
                    { id: true, format: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Incoming) },
                    { id: false, format: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Outgoing) }
                ],
                valueExpr: this.idColumnName,
                displayExpr: this.formatColumnName
            }
        })
        columns.push(<any>{ id: this.lastUpdateColumnName, caption: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.PacketTime), type: Enums.DataTypeClass.Datetime })
        columns.push(<any>{ id: this.serialNbrColumnName, caption: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.SerialNbr) })
        columns.push(<any>{ id: this.transmissionDriverColumnName, caption: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.TransmissionDriver) })
        columns.push(<any>{ id: this.packetStatusColumnName, caption: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Status) })

        deferred.resolve(columns)

        return deferred.promise()
    }

    public getActionPacketDataColumns(): JQueryPromise<ColumnModel[]> {
        const deferred: JQueryDeferred<ColumnModel[]> = $.Deferred<ColumnModel[]>()

        const columns: ColumnModel[] = []
        columns.push(<any>{ id: this.dataTypeColumnName, caption: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.DataType) })
        columns.push(<any>{ id: this.indexColumnName, caption: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Index) })
        columns.push(<any>{ id: this.statusColumnName, caption: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Status) })
        columns.push(<any>{ id: this.timeColumnName, caption: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Time), type: Enums.DataTypeClass.Datetime })
        columns.push(<any>{ id: this.valueColumnName, caption: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Value) })

        deferred.resolve(columns)

        return deferred.promise()
    }

    public getActionDetailedParametersGridColumns(): DevExpress.ui.dxDataGridColumn[] {
        return [{
            dataField: this.dataTypeColumnName,
            caption: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Description)
        },
        {
            dataField: this.indexNbrColumnName,
            caption: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Index)
        },
        {
            dataField: this.valueColumnName,
            caption: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Value),
            dataType: "string",
            calculateDisplayValue: (rowData: any) => {
                return CommonHelper.cellFormatForDifferentValues(this.userSession, rowData, null, null)
            }
        }]
    }

    public get addresColumnName(): string { return 'address' }
    public get dataTypeColumnName(): string { return 'dataType' }
    public get formatColumnName(): string { return 'format' }
    public get idColumnName(): string { return 'id' }
    public get indexColumnName(): string { return 'index' }
    public get indexNbrColumnName(): string { return 'indexNbr' }
    public get isIncomingColumnName(): string { return 'isIncoming' }
    public get lastUpdateColumnName(): string { return 'lastUpdate' }
    public get packetStatusColumnName(): string { return 'packetStatus' }
    public get serialNbrColumnName(): string { return 'serialNbr' }
    public get statusColumnName(): string { return 'status' }
    public get timeColumnName(): string { return 'time' }
    public get transmissionDriverColumnName(): string { return 'transmissionDriver' }
    public get valueColumnName(): string { return 'value' }

    /**to think how to put color into theme */
    public get greenColor(): string { return '#02E6A1' }
    /**to think how to put color into theme */
    public get redColor(): string { return '#F7484E' }

    // #endregion


    // #region Private Methods


    // #endregion





}

export class ActionServiceProvider
    implements ng.IServiceProvider {

    //#region IServiceProvider Implementation 

    public $get = [USER_SESSION_SERVICE_NAME, GRID_SERVICE_NAME, function (userSession: UserSessionService, gridService: GridService) {
        return new ActionService(userSession, gridService)
    }]

    //#endregion
} 
